/**
 * useWebSearch Hook
 * 
 * Handles web search functionality including:
 * - Google Custom Search API integration
 * - Product extraction from conversation
 * - Internal catalog search with profile matching
 * - In-house product prioritization
 * - Search result state management
 * 
 * Extracted from App.js to reduce file size.
 * 
 * @author Heseda AI Team
 * @version 1.0
 * Added in-house product matching
 */

import { useState, useCallback, useRef, useEffect } from 'react';
import axios from 'axios';
import { auth, db } from '../services/firebaseConfig';
import { doc, setDoc } from 'firebase/firestore';
import { calculateProfileCompletion } from '../services/profileService';
import { translateKeywordToEnglish, translateKeywordToKorean } from '../utils/searchKeywordUtils';
import { getAuthHeaders } from '../services/apiAuth';
import { findMatchingProducts, mergeProductSources } from '../services/productMatcher';
import { buildAffiliateUrl, type AffiliateNetwork } from '../utils/affiliateDistribution';
import { getApiBaseUrl } from '../utils/apiConfig';
import { hasEmbeddings, getProfileEmbedding, getEmbeddingSimilarityMap } from '../services/embeddingService';
import { INHOUSE_SELLING_ENABLED } from '../config/adminConfig';
import { isInPhase1, getPhase1SearchBlockedMessage } from '../utils/phaseUtils';
import { detectUserRegion, getSearchParams, getAmazonTagForRegion, getRegionConfig, type SupportedRegion } from '../config/affiliateConfig';
import type { MasterProfile, ProductSource } from '../types/profile';
import type { ChatMessage, LanguageCode, SearchTriggerSource, WebSearchResultItem } from '../types';
import type { NormalizedProduct } from '../services/catalogService';
import { trackSearch } from '../services/activityTrackingService';
import { extractBoldKeywords } from './extractBoldKeywords';
import { sanitizeChatMessages } from '../utils/messageSanitizer';
import { isRelevantBeautyProduct } from '../utils/productTitle';
import {
  extractConversationContext,
  buildCompoundQuery,
  isAmazonProductPage,
  deduplicateByAsin,
} from '../utils/searchQueryEnricher';

// ============================================================================
// Affiliate Configuration
// ============================================================================

/** Non-Amazon affiliate codes (region-independent) */
const BASE_AFFILIATE_CODES = {
  iherb: import.meta.env.VITE_IHERB_AFFILIATE_CODE || '',
  coupang: import.meta.env.VITE_COUPANG_PARTNER_ID || '',
  yesstyle: import.meta.env.VITE_YESSTYLE_AFFILIATE_ID || '',
};

/** Build region-aware affiliate codes (Amazon tag varies by region) */
function getAffiliateCodes(region: SupportedRegion) {
  return {
    amazon: getAmazonTagForRegion(region),
    ...BASE_AFFILIATE_CODES,
  };
}

/**
 * Detect affiliate network from URL domain
 */
function detectAffiliateNetwork(url: string): AffiliateNetwork | null {
  try {
    const hostname = new URL(url).hostname.toLowerCase();
    if (hostname.includes('amazon.')) return 'amazon';
    if (hostname.includes('iherb.')) return 'iherb';
    if (hostname.includes('coupang.')) return 'coupang';
    if (hostname.includes('yesstyle.')) return 'yesstyle';
    return null;
  } catch {
    return null;
  }
}

/**
 * Add affiliate tag to URL if applicable.
 * 
 * Logs warnings when tagging is skipped due to missing configuration
 * to help diagnose affiliate revenue tracking issues in production.
 */
function addAffiliateTag(url: string, region: SupportedRegion): string {
  const network = detectAffiliateNetwork(url);
  if (!network) return url;

  const codes = getAffiliateCodes(region);
  const affiliateCode = codes[network];
  if (!affiliateCode) {
    console.warn('[affiliate] Skipping tagging: missing code for', network);
    return url;
  }

  try {
    return buildAffiliateUrl(url, network, codes);
  } catch (error) {
    console.error('[affiliate] Failed to build URL', { network, error });
    return url;
  }
}

// ============================================================================
// Types
// ============================================================================

/** Google Custom Search API response item */
interface GoogleSearchItem {
  title: string;
  snippet: string;
  link: string;
  displayLink?: string;
  pagemap?: {
    cse_image?: Array<{ src: string }>;
    cse_thumbnail?: Array<{ src: string }>;
    metatags?: Array<Record<string, string>>;
    offer?: Array<{ price?: string; pricecurrency?: string }>;
    product?: Array<{ price?: string; pricecurrency?: string }>;
  };
}

/** Web search result from Google Custom Search */
export interface WebSearchResult {
  name: string;
  snippet: string;
  link: string;
  displayLink: string;
  image?: string;
  price?: string;
  currency: string;
  reason?: string; // Korean keyword that triggered this result
  source?: ProductSource; // Product source for business prioritization
}

/** Extended MasterProfile with runtime AI analysis fields */
interface ExtendedMasterProfile extends MasterProfile {
  aiAnalysis?: {
    searchKeywords?: string[];
    skinType?: string;
    lastAnalysisDate?: string;
    [key: string]: unknown;
  };
}

/** Options for useWebSearch hook */
export interface UseWebSearchOptions {
  /** Current language */
  language: LanguageCode;
  /** Session ID for API calls */
  sessionId: string;
  /** Current master profile (with potential runtime aiAnalysis) */
  masterProfile: ExtendedMasterProfile;
  /** Internal product catalog */
  catalog: NormalizedProduct[];
  /** Function to add messages to chat */
  addMessage: (message: ChatMessage) => void;
  /** Function to refresh editor picks (optional - can be set via ref) */
  handleRefreshEditorPicks?: ((profile: ExtendedMasterProfile) => void) | null;
  /** Ref to handleRefreshEditorPicks for delayed binding */
  handleRefreshEditorPicksRef?: React.MutableRefObject<((profile: ExtendedMasterProfile) => void) | null>;
  /** Translation helper */
  t: (ko: string, en: string) => string;
  /** Whether to use RAG vector similarity for in-house product matching (default: true) */
  useRag?: boolean;
  /** Callback when search completes with results (for voice context sync) */
  onSearchComplete?: (results: Array<{
    name: string;
    link?: string;
    price?: string | number;
    reason?: string;
  }>) => void;
}

/** Return type for useWebSearch hook */
export interface UseWebSearchReturn {
  /** Current web search results */
  webResults: WebSearchResult[];
  /** Whether search is in progress */
  isSearching: boolean;
  /** Clear web results */
  clearWebResults: () => void;
  /** Perform web search */
  handleSearch: (messages: ChatMessage[], hasUserInteracted: boolean, triggerSource?: SearchTriggerSource, userQueryText?: string) => Promise<void>;
  /** Extract recommended products from conversation */
  extractRecommendedProducts: (conversation: ChatMessage[]) => Promise<string[]>;
  /** Search internal catalog */
  searchInternalCatalog: (query: string) => Promise<unknown[]>;
}

// ============================================================================
// Constants
// ============================================================================

/** Global cosmetic store domains used across all regions */
const GLOBAL_BEAUTY_STORES = [
  'iherb.com',
  'paulaschoice.com',
  'theordinary.com',
  'cerave.com',
  'lookfantastic.com',
];

/** Cosmetic store domains by region (keyed by SupportedRegion) */
const COSMETIC_STORES: Record<SupportedRegion, string[]> = {
  US: [
    'ulta.com', 'sephora.com', 'target.com', 'walmart.com', 'cvs.com',
    'walgreens.com', 'dermstore.com', 'skinstore.com', 'bluemercury.com',
    'spacenk.com', 'lovelyskin.com', 'skincarerx.com',
    'thedetoxmarket.com', 'credobeauty.com', 'wholefoodsmarket.com',
    'vitacost.com', 'kiehl.com', 'laroche-posay.us',
    ...GLOBAL_BEAUTY_STORES,
  ],
  KR: [
    'innisfree.com', 'yesstyle.com', 'sokoglam.com', 'stylekorean.com',
    'stylevana.com', 'beautynetkorea.com', 'livekbeauty.com', 'dodoskin.com',
    'koreanbeautycosmetics.com', 'jolse.com', 'roseroseshop.com',
    'beautyboxkorea.com', 'testerkorea.com', 'cosmetic-love.com',
    ...GLOBAL_BEAUTY_STORES,
  ],
  BR: [
    'belezanaweb.com.br', 'epocacosmeticos.com.br', 'sephora.com.br',
    'oboticario.com.br', 'natura.com.br', 'dermage.com.br',
    'laroche-posay.com.br', 'cerave.com.br',
    ...GLOBAL_BEAUTY_STORES,
  ],
  MX: [
    'sephora.com.mx', 'liverpool.com.mx', 'palaciolastiendas.com',
    'laroche-posay.com.mx', 'cerave.com.mx',
    ...GLOBAL_BEAUTY_STORES,
  ],
  SG: [
    'sephora.sg', 'guardian.com.sg', 'watsons.com.sg',
    'lookfantastic.com.sg', 'beautycarousel.com',
    ...GLOBAL_BEAUTY_STORES,
  ],
  CA: [
    'sephora.ca', 'shoppersdrugmart.ca', 'well.ca', 'londondrugs.com',
    ...GLOBAL_BEAUTY_STORES,
  ],
  UK: [
    'boots.com', 'superdrug.com', 'spacenk.com', 'lookfantastic.com',
    'cultbeauty.co.uk', 'feelunique.com', 'sephora.co.uk',
    ...GLOBAL_BEAUTY_STORES,
  ],
  DE: [
    'douglas.de', 'flaconi.de', 'sephora.de', 'dm.de',
    ...GLOBAL_BEAUTY_STORES,
  ],
  FR: [
    'sephora.fr', 'nocibe.fr', 'marionnaud.fr', 'beaute-test.com',
    ...GLOBAL_BEAUTY_STORES,
  ],
  IT: [
    'sephora.it', 'douglas.it', 'lookfantastic.it',
    ...GLOBAL_BEAUTY_STORES,
  ],
  ES: [
    'sephora.es', 'douglas.es', 'primor.eu', 'druni.es',
    ...GLOBAL_BEAUTY_STORES,
  ],
  NL: [
    'douglas.nl', 'ici-paris-xl.nl', 'lookfantastic.nl',
    ...GLOBAL_BEAUTY_STORES,
  ],
  SE: [
    'kicks.se', 'lyko.se', 'bangerhead.se',
    ...GLOBAL_BEAUTY_STORES,
  ],
  PL: [
    'douglas.pl', 'sephora.pl', 'hebe.pl', 'notino.pl',
    ...GLOBAL_BEAUTY_STORES,
  ],
};

// ============================================================================
// Firebase Helpers
// ============================================================================

/**
 * Save search context to Firebase for learning and personalization
 * @param keywords - Search keywords used
 * @param results - Search results (titles, links, sources)
 */
async function saveSearchContext(
  keywords: string[],
  results: Array<{ name: string; link: string; source?: ProductSource }>
): Promise<void> {
  const user = auth.currentUser;
  if (!user) {
    console.warn('No authenticated user - skipping search context save');
    return;
  }

  try {
    // Phase 2: write search context to masterProfiles instead of users
    const profileRef = doc(db, 'masterProfiles', user.uid);
    
    // Build search result summary for this search session
    const searchId = `search_${Date.now()}`;
    const searchSummary = {
      searchId,
      keywords: keywords.slice(0, 5), // Top 5 keywords for this search
      timestamp: new Date().toISOString(),
      results: results.slice(0, 5).map(r => ({
        name: r.name,
        source: r.source || 'web',
        clicked: false, // Will be updated when user clicks
      })),
    };

    // Build search context update
    const searchContextUpdate = {
      recentKeywords: keywords.slice(0, 10), // Last 10 keywords
      lastSearchAt: new Date().toISOString(),
    };

    // Merge with existing preferenceEngine.searchContext
    await setDoc(profileRef, {
      preferenceEngine: {
        searchContext: searchContextUpdate,
        lastSearch: searchSummary, // Store latest search separately for easy access
      },
    }, { merge: true });
  } catch (error) {
    console.error('Error saving search context:', error);
    // Non-blocking - don't throw
  }
}

// ============================================================================
// Hook Implementation
// ============================================================================

/**
 * Hook for handling web search operations
 * 
 * @param options - Configuration options
 * @returns Object with search state and functions
 * 
 * @example
 * ```tsx
 * const { webResults, isSearching, handleSearch, clearWebResults } = useWebSearch({
 *   language,
 *   sessionId,
 *   masterProfile,
 *   catalog,
 *   addMessage,
 *   handleRefreshEditorPicks,
 *   t,
 * });
 * ```
 */
export function useWebSearch(options: UseWebSearchOptions): UseWebSearchReturn {
  const {
    language,
    sessionId,
    masterProfile,
    catalog,
    addMessage,
    handleRefreshEditorPicks,
    handleRefreshEditorPicksRef,
    t,
    useRag = true,
    onSearchComplete,
  } = options;



  // Use ref to always access latest profile (avoids stale closure issues)
  const masterProfileRef = useRef(masterProfile);
  useEffect(() => {
    masterProfileRef.current = masterProfile;
  }, [masterProfile]);

  // State
  const [webResults, setWebResults] = useState<WebSearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  /**
   * Clear web search results
   */
  const clearWebResults = useCallback(() => {
    setWebResults([]);
  }, []);

  /**
   * Extract recommended products from conversation using AI
   */
  const extractRecommendedProducts = useCallback(
    async (conversation: ChatMessage[]): Promise<string[]> => {
      try {
        const headers = await getAuthHeaders();
        const response = await axios.post<string>('/api/getOpenAIResponse', {
          prompt: `Analyze the conversation and extract specific cosmetic products, product types, manufacturers, or brands. Return them as a space-separated list of groups, each group separated by a comma.`,
          sessionId,
          messages: sanitizeChatMessages(conversation),
          mode: 'extractProducts',
          language,
        }, { headers });

        if (response?.data) {
          const productGroups = String(response.data).trim().split(',');
          return productGroups.map((group: string) => group.trim());
        } else {
          throw new Error('Failed to extract recommended products.');
        }
      } catch (error) {
        console.error('Error extracting products:', error);
        return ['general cosmetic products'];
      }
    },
    [sessionId, language]
  );

  /**
   * Search internal catalog for matching products
   */
  const searchInternalCatalog = useCallback(
    async (query: string): Promise<unknown[]> => {
      try {
        if (!catalog || catalog.length === 0) {
          return [];
        }

        const queryLower = query.toLowerCase();
        const keywords = queryLower.split(' ').filter((k) => k.length > 1);

        // Score products by keyword matches
        const scoredResults = catalog
          .map((product) => {
            const searchableText = [
              product.name,
              product.category,
              product.description,
              ...(product.tags || []),
              ...(product.keyIngredients || []),
            ]
              .join(' ')
              .toLowerCase();

            const matchCount = keywords.filter((keyword) =>
              searchableText.includes(keyword)
            ).length;

            return { product, matchCount };
          })
          .filter((item) => item.matchCount > 0);

        // Sort by relevance
        scoredResults.sort((a, b) => b.matchCount - a.matchCount);

        const results = scoredResults.map((item) => item.product);

        return results.slice(0, 15);
      } catch (error) {
        console.error('Internal catalog search error:', error);
        return [];
      }
    },
    [catalog]
  );

  /**
   * Parse a single Google search item into a WebSearchResult.
   */
  const parseSearchItem = useCallback(
    (item: GoogleSearchItem, userRegion: SupportedRegion, reason?: string): WebSearchResult => {
      const image =
        item.pagemap?.cse_image?.[0]?.src ||
        item.pagemap?.metatags?.[0]?.['og:image'] ||
        item.pagemap?.cse_thumbnail?.[0]?.src;

      const price =
        item.pagemap?.offer?.[0]?.price ||
        item.pagemap?.product?.[0]?.price ||
        item.pagemap?.metatags?.[0]?.['product:price:amount'];

      const currency =
        item.pagemap?.offer?.[0]?.pricecurrency ||
        item.pagemap?.product?.[0]?.pricecurrency ||
        item.pagemap?.metatags?.[0]?.['product:price:currency'] ||
        getRegionConfig(userRegion).currency;

      const affiliateLink = addAffiliateTag(item.link, userRegion);

      return {
        name: item.title,
        snippet: item.snippet,
        link: affiliateLink,
        displayLink: item.displayLink || new URL(item.link).hostname,
        image,
        price,
        currency,
        reason,
        source: 'web' as ProductSource,
      };
    },
    []
  );

  /**
   * Perform Google search for product keywords via backend proxy.
   *
   * Hybrid search strategy (Row 1-2 Amazon/affiliate CPC + Row 3 open web fallback):
   *   Phase 1 — Affiliate-restricted search (Amazon / Coupang per region)
   *              Requests multiple results, prefers /dp/ product pages, ASIN-deduped.
   *   Phase 2 — Open web fallback for groups that returned 0 affiliate results
   */
  const performGoogleSearch = useCallback(
    async (searchQuery: string, keywordReasons: string[] = [], triggerSource?: SearchTriggerSource, conversationCtx: string[] = []): Promise<void> => {
      const productGroups = searchQuery.split(',').map((g) => g.trim());

      const allResults: WebSearchResult[] = [];

      // Backend API URL - platform-aware for native apps
      const API_URL = getApiBaseUrl();

      // Geo-aware: resolve region from user profile (constant across iterations)
      const currentProfile = masterProfileRef.current;
      const userRegion = detectUserRegion(currentProfile?.identity?.country);
      const searchGeo = getSearchParams(userRegion, language);
      const regionConfig = getRegionConfig(userRegion);

      // Determine the primary affiliate site for this region
      const affiliateSite = regionConfig.amazon?.domain
        || (regionConfig.primaryNetwork === 'coupang' ? 'coupang.com' : 'amazon.com');

      const searchHeaders = await getAuthHeaders();

      // Language restriction: use lr param to prefer results in the user's language
      // This prevents e.g. Chinese product listings when searching in Korean
      const lr = language && language !== 'en' ? `lang_${language.split('-')[0]}` : undefined;

      // Target tile count: 6 (desktop 3×2) — dynamic columns handle mobile (→ 2×2)
      const TILE_TARGET = 6;

      // Track affiliate results separately for fill-first strategy
      const affiliateResults: WebSearchResult[] = [];
      const missedGroups: Array<{ group: string; reason?: string }> = [];

      // --- Phase 1: Affiliate-restricted search (Amazon/Coupang) — fill first ---
      // Request extra results (num:4) so we can filter /s? pages and ASIN-dedup
      for (let i = 0; i < productGroups.length; i++) {
        if (i >= TILE_TARGET || affiliateResults.length >= TILE_TARGET) break;
        const group = productGroups[i];
        const reason = keywordReasons[i];

        // Build compound query: append profile context for richer results
        const compoundGroup = buildCompoundQuery(group, currentProfile, conversationCtx, language);

        try {
          const slotsRemaining = TILE_TARGET - affiliateResults.length;
          const response = await axios.post<{ items?: GoogleSearchItem[]; error?: string }>(
            `${API_URL}/googleSearch`,
            {
              query: `${language === 'ko' ? '화장품' : 'skincare'} ${compoundGroup}`,
              num: Math.min(4, slotsRemaining),
              siteRestrict: affiliateSite,
              gl: searchGeo.gl,
              hl: searchGeo.hl,
              ...(lr && { lr }),
            },
            { headers: searchHeaders, timeout: 15000 }
          );

          if (response.data?.items && response.data.items.length > 0) {
            // Take ALL valid product-page results from affiliate, not just first
            let foundProduct = false;
            for (const item of response.data.items) {
              if (affiliateResults.length >= TILE_TARGET) break;
              if (!isAmazonProductPage(item.link)) continue;
              if (!isRelevantBeautyProduct(item.title || '', item.link, item.snippet || '')) continue;
              const parsed = parseSearchItem(item, userRegion, reason);
              parsed.source = 'partner' as ProductSource;
              affiliateResults.push(parsed);
              foundProduct = true;
            }
            if (!foundProduct) {
              // All items are non-product pages — treat as miss for Phase 2 fallback
              missedGroups.push({ group: compoundGroup, reason });
            }
          } else {
            missedGroups.push({ group: compoundGroup, reason });
          }
        } catch (error: any) {
          console.warn(`Affiliate search failed for "${group}":`, error.message);
          missedGroups.push({ group: compoundGroup, reason });
        }
      }

      // --- Phase 2: Open-web fallback ONLY for remaining empty slots ---
      const fallbackSlots = TILE_TARGET - affiliateResults.length;
      const fallbackResults: WebSearchResult[] = [];

      if (fallbackSlots > 0 && missedGroups.length > 0) {
        const fallbackDomains = COSMETIC_STORES[userRegion] || COSMETIC_STORES.US;
        const siteSearchQuery = fallbackDomains
          .map((domain) => `site:${domain}`)
          .join(' OR ');

        for (const { group, reason } of missedGroups.slice(0, fallbackSlots)) {
          if (fallbackResults.length >= fallbackSlots) break;
          try {
            const response = await axios.post<{ items?: GoogleSearchItem[]; error?: string }>(
              `${API_URL}/googleSearch`,
              {
                query: `${language === 'ko' ? '화장품' : 'skincare'} ${group} ${siteSearchQuery}`,
                num: 1,
                gl: searchGeo.gl,
                hl: searchGeo.hl,
                ...(lr && { lr }),
              },
              { headers: searchHeaders, timeout: 15000 }
            );

            if (response.data?.items && response.data.items.length > 0) {
              fallbackResults.push(parseSearchItem(response.data.items[0], userRegion, reason));
            }
          } catch (error: any) {
            console.warn(`Fallback search failed for "${group}":`, error.message);
          }
        }
      }

      // Affiliate first, external fills remaining — never external-heavy
      allResults.push(...affiliateResults, ...fallbackResults);

      // Filter out bad Amazon pages (search listings, live, stores) and
      // irrelevant products (books, movies, electronics from ads)
      const cleanResults = allResults.filter(r => {
        if (!isRelevantBeautyProduct(r.name || '', r.link, r.snippet || '')) return false;
        const lower = r.link.toLowerCase();
        if (lower.includes('amazon.')) return isAmazonProductPage(r.link);
        return true;
      });
      const filteredResults = deduplicateByAsin(cleanResults);

      // --- Phase 3: Broad beauty fallback when all results were filtered out ---
      // Search without site restrictions for relevant beauty products near user's region
      if (filteredResults.length === 0 && productGroups.length > 0) {
        const broadQuery = `${language === 'ko' ? '스킨케어 추천' : 'best skincare'} ${productGroups[0]}`;
        try {
          const broadResp = await axios.post<{ items?: GoogleSearchItem[]; error?: string }>(
            `${API_URL}/googleSearch`,
            {
              query: broadQuery,
              num: 4,
              gl: searchGeo.gl,
              hl: searchGeo.hl,
              ...(lr && { lr }),
            },
            { headers: searchHeaders, timeout: 15000 }
          );
          if (broadResp.data?.items) {
            for (const item of broadResp.data.items) {
              if (filteredResults.length >= TILE_TARGET) break;
              if (!isRelevantBeautyProduct(item.title || '', item.link, item.snippet || '')) continue;
              const parsed = parseSearchItem(item, userRegion, keywordReasons[0]);
              parsed.source = 'web' as ProductSource;
              filteredResults.push(parsed);
            }
          }
        } catch (err: any) {
          console.warn('Broad beauty fallback search failed:', err.message);
        }
      }

      // Compute RAG embedding similarity if enabled (only useful with in-house catalog)
      let embeddingSimilarityMap: Map<string, number> | undefined;
      if (INHOUSE_SELLING_ENABLED && useRag && hasEmbeddings()) {
        try {
          const profileEmbedding = await getProfileEmbedding(currentProfile as any);
          if (profileEmbedding) {
            embeddingSimilarityMap = getEmbeddingSimilarityMap(profileEmbedding);
            if (import.meta.env.DEV) {
              console.log(`📊 RAG: vector similarity computed for ${embeddingSimilarityMap.size} products`);
            }
          }
        } catch (err) {
          console.warn('⚠️ RAG embedding unavailable, falling back to traditional:', err);
        }
      }

      // Find matching in-house products from internal catalog (only when in-house selling is enabled)
      const internalMatches = INHOUSE_SELLING_ENABLED
        ? findMatchingProducts(catalog, currentProfile, {
            maxResults: 3,
            minScore: 25,
            embeddingSimilarityMap,
          })
        : [];

      // Merge internal products (first) with web results
      // Design: in-house Row 1 + affiliate/web Row 2 when in-house selling enabled
      // Affiliate-only mode: up to 6 tiles (affiliate first, external fills remainder)
      const maxMerged = TILE_TARGET;
      const mergedResults = mergeProductSources(internalMatches, filteredResults.map(r => ({
        ...r,
        source: r.source || ('web' as ProductSource),
      })), maxMerged);

      // Update state with merged results
      if (mergedResults.length > 0) {
        setWebResults(filteredResults); // Keep filtered web results in state for reference

        const inhouseCount = internalMatches.length;

        // Save search context to Firebase for learning (non-blocking)
        const searchKeywords = productGroups;
        const searchResultsForSave = mergedResults
          .filter(r => r.name) // Only include results with titles
          .map(r => ({
            name: r.name!,
            link: r.link,
            source: r.source,
          }));
        saveSearchContext(searchKeywords, searchResultsForSave).catch(err => 
          console.warn('Failed to save search context:', err)
        );

        // Add merged results as a web-search type message (inline collapsible)
        addMessage({
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
          sender: 'bot',
          type: 'web-search',
          text: inhouseCount > 0
            ? t(
                `${mergedResults.length}개의 맞춤 제품을 찾았습니다. (자체 추천 ${inhouseCount}개 포함)`,
                `Found ${mergedResults.length} products. (${inhouseCount} from our catalog)`
              )
            : t(
                `${mergedResults.length}개의 맞춤 제품을 찾았습니다.`,
                `Found ${mergedResults.length} products for your concerns.`
              ),
          webSearchResults: mergedResults,
          searchTriggerSource: triggerSource ?? 'manual',
        });

        // Notify voice session about search results (for context sync)
        if (onSearchComplete) {
          onSearchComplete(mergedResults.slice(0, 5).map(r => ({
            name: r.name || 'Unknown Product',
            link: r.link,
            price: r.price,
            reason: r.reason,
          })));
        }
      } else {
        // Clear stale results from previous search (prevents UI showing old tiles)
        setWebResults([]);

        // Save empty search context for learning even on no-results
        saveSearchContext(productGroups, []).catch(err =>
          console.warn('Failed to save empty search context:', err)
        );

        // Even when no results found, post web-search container so sponsored tiles render
        addMessage({
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
          sender: 'bot',
          type: 'web-search',
          text: t(
            '이 카테고리의 제품을 찾지 못했어요. 다른 키워드로 다시 검색하거나, 원하시는 제품 조건을 알려주세요!',
            'No products found for this search. Try different keywords, or tell me what you\'re looking for and I\'ll help!'
          ),
          webSearchResults: [],
          searchTriggerSource: triggerSource ?? 'manual',
        });
      }
    },
    [language, catalog, addMessage, t, useRag, parseSearchItem]
  );

  /**
   * Main search handler - orchestrates the full search flow
   * Uses ref to always get latest profile (fixes stale closure issue with auto-search)
   */
  const handleSearch = useCallback(
    async (messages: ChatMessage[], hasUserInteracted: boolean, triggerSource?: SearchTriggerSource, userQueryText?: string): Promise<void> => {
      // G11: Early offline check
      if (!navigator.onLine) {
        addMessage({
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
          sender: 'bot',
          text: language === 'en'
            ? '📶 You appear to be offline. Please check your connection and try again.'
            : '📶 오프라인 상태입니다. 연결을 확인하고 다시 시도해주세요.',
        });
        return;
      }

      // Use ref to get latest profile (avoids stale closure from setTimeout)
      const currentProfile = masterProfileRef.current;

      // Phase 1 check: Block Web Search if profile is still in Phase 1
      const completion = calculateProfileCompletion(currentProfile);
      if (isInPhase1(completion)) {
        addMessage({
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
          sender: 'bot',
          text: `⚠️ ${getPhase1SearchBlockedMessage(completion, language)}`,
        });
        return;
      }

      // Allow search if we have profile signals from Firebase
      const hasProfileSignals = Boolean(
        currentProfile?.aiAnalysis?.searchKeywords?.length ||
          currentProfile?.skin?.type ||
          currentProfile?.skin?.concerns?.length
      );

      if (!hasUserInteracted && !hasProfileSignals) {
        addMessage({
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
          sender: 'bot',
          text:
            language === 'en'
              ? 'Please enter a message before using the search feature.'
              : '검색 기능을 사용하기 전에 메시지를 입력해 주세요.',
        });
        return;
      }

      setIsSearching(true);

      try {
        // Extract contextual modifiers from recent user messages (baby, fragrance-free, etc.)
        const conversationCtx = extractConversationContext(messages, language);

        // Extract search keywords — prioritize user's explicit query text
        let searchKeywords: string[] = [];

        // 0. PRIMARY: Use user's explicit query text when available
        //    This is the text the user typed in the chat input (Search button or Send)
        //    It's the most reliable signal of what the user actually wants to search for.
        const userText = userQueryText?.trim();
        if (userText) {
          // Use the user's text directly as a single search query (keeps brand + product together)
          searchKeywords = [userText];

          // Diversify: if single short query, add concern-based variants for extra slots
          // e.g. user types "serum" → ["serum", "acne serum", "hydrating serum"]
          if (userText.split(/\s+/).length <= 2 && currentProfile?.skin?.concerns?.length) {
            for (const concern of currentProfile.skin.concerns.slice(0, 2)) {
              const variant = `${concern} ${userText}`;
              if (!searchKeywords.includes(variant)) {
                searchKeywords.push(variant);
              }
            }
          }
        } else {
          // FALLBACK: Extract from AI recommendations + profile when no explicit user text
          searchKeywords = extractBoldKeywords(messages);

          // 1. Use AI analysis keywords if available, filtered by user's UI language
          if (currentProfile?.aiAnalysis?.searchKeywords?.length) {
            const profileKeywords = currentProfile.identity?.uiLanguage === 'ko'
              ? currentProfile.aiAnalysis.searchKeywords.filter(keyword => !/[a-zA-Z]/.test(keyword))
              : currentProfile.aiAnalysis.searchKeywords.filter(keyword => !/[가-힣]/.test(keyword));
            for (const kw of profileKeywords) {
              if (!searchKeywords.includes(kw)) {
                searchKeywords.push(kw);
              }
            }
          }

          // 2. Add keywords from skin profile
          if (currentProfile?.skin?.type) {
            if (!searchKeywords.includes(currentProfile.skin.type)) {
              searchKeywords.push(currentProfile.skin.type);
            }
          }
          if (currentProfile?.skin?.concerns?.length) {
            const newConcerns = currentProfile.skin.concerns.filter(
              (c: string) => !searchKeywords.includes(c)
            );
            searchKeywords.push(...newConcerns);
          }

          // 3. Extract from conversation if no profile data yet
          if (searchKeywords.length === 0) {
            const productGroups = await extractRecommendedProducts(messages);
            searchKeywords = productGroups;
          }
        }

        if (searchKeywords.length === 0) {
          addMessage({
            id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
            timestamp: new Date().toISOString(),
            sender: 'bot',
            text:
              language === 'en'
                ? 'Please tell me about your skin concerns or upload a photo first to get personalized product recommendations.'
                : '맞춤 제품 추천을 받으려면 먼저 피부 고민을 말씀해주시거나 사진을 업로드해주세요.',
          });
          return;
        }

        // Build search queries from keywords (supports both Korean and English)
        const searchQueries: string[] = [];
        const keywordReasons: string[] = [];

        if(currentProfile.identity?.uiLanguage === 'ko'){
          for (const keyword of searchKeywords){
            const isEnglish = /[a-zA-Z]/.test(keyword);
            if (isEnglish) {
              const koreanTerms = translateKeywordToKorean(keyword);
              if (koreanTerms.length > 0) {
                koreanTerms.forEach((koreanKeyword) => {
                  searchQueries.push(koreanKeyword);
                  keywordReasons.push(keyword);
                });
              } else {
                // Brand/product names (e.g. "COSRX snail mucin") won't translate —
                // use the original English text so Google can still search for it.
                searchQueries.push(keyword);
                keywordReasons.push(keyword);
              }
            }else{
              // Korean keyword - use directly
              searchQueries.push(keyword);
              keywordReasons.push(keyword);
            }
          }
        }else{
          for (const keyword of searchKeywords) {
            const isKorean = /[가-힣]/.test(keyword);
            if (isKorean) {
              // Korean keyword - translate to English for Google search
              const englishTerms = translateKeywordToEnglish(keyword);
              if (englishTerms.length > 0) {
                searchQueries.push(englishTerms.slice(0, 3).join(' '));
                keywordReasons.push(keyword);
              }
            } else {
              // English keyword - use directly
              searchQueries.push(keyword);
              keywordReasons.push(keyword);
            }
          }
        }

        // Deduplicate queries while preserving aligned keywordReasons
        // e.g. ["acne", "acne treatment"] → ["acne treatment"]
        const seen = new Set<string>();
        const dedupedQueries: string[] = [];
        const dedupedReasons: string[] = [];
        for (let i = 0; i < searchQueries.length; i++) {
          if (!seen.has(searchQueries[i])) {
            seen.add(searchQueries[i]);
            dedupedQueries.push(searchQueries[i]);
            dedupedReasons.push(keywordReasons[i]);
          }
        }
        // Remove substrings absorbed by longer queries
        const uniqueQueries: string[] = [];
        const uniqueReasons: string[] = [];
        for (let i = 0; i < dedupedQueries.length; i++) {
          const isSubstring = dedupedQueries.some(
            (other) => other !== dedupedQueries[i] && other.includes(dedupedQueries[i])
          );
          if (!isSubstring) {
            uniqueQueries.push(dedupedQueries[i]);
            uniqueReasons.push(dedupedReasons[i]);
          }
        }
        // User activity
        uniqueQueries.forEach(query => {
          trackSearch(query);
        });

        if (uniqueQueries.length === 0) {
          addMessage({
            id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
            timestamp: new Date().toISOString(),
            sender: 'bot',
            text:
              language === 'en'
                ? 'Could not identify specific skin concerns for search.'
                : '검색할 피부 고민을 찾을 수 없습니다.',
          });
          return;
        }

        // Trigger Editor's Picks (non-blocking)
        // Use ref if available (for delayed binding), otherwise use direct callback
        const refreshFn = handleRefreshEditorPicksRef?.current ?? handleRefreshEditorPicks;
        if (refreshFn) {
          refreshFn(currentProfile);
        }

        // Search Google for best product per keyword
        await performGoogleSearch(uniqueQueries.join(','), uniqueReasons, triggerSource, conversationCtx);
      } catch (error) {
        console.error('❌ Search error:', error);
        addMessage({
          id: `${auth.currentUser?.email}:${Date.now()}:${Math.random().toString(36).slice(2,8)}`,
          timestamp: new Date().toISOString(),
          sender: 'bot',
          text:
            language === 'en'
              ? 'Search failed. Please try again.'
              : '검색에 실패했습니다. 다시 시도해주세요.',
        });
      } finally {
        setIsSearching(false);
      }
    },
    [
      language,
      addMessage,
      extractRecommendedProducts,
      handleRefreshEditorPicks,
      handleRefreshEditorPicksRef,
      performGoogleSearch,
    ]
  );

  return {
    webResults,
    isSearching,
    clearWebResults,
    handleSearch,
    extractRecommendedProducts,
    searchInternalCatalog,
  };
}

export default useWebSearch;
